import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useWebApp } from '@/providers/WebAppProvider';
import { removeReqHeader, selectReqHeader } from '@/redux/reducers/common';
import { CloseWebPopUpData } from '@/types';
import { useRouter } from 'next/router';
import { getSessionItem, setSessionItem } from '@remodeling/base';

/**
 * 백버튼 이벤트 처리
 * @author 이현우
 * @returns
 */
const useBackHandler = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const reqHeader = useSelector(selectReqHeader);
  const { isApp, handleCloseWebPopUp } = useWebApp();

  /**
   * referer가 공백인지 확인
   */
  const isEmptyReferer: boolean = useMemo(() => {
    return !!(
      !reqHeader?.referer ||
      (reqHeader?.referer && reqHeader.referer === '')
    );
  }, [reqHeader]);

  /**
   * referer가 있으면, hanssem과 utmSourcer가 있는지 확인
   */
  const isNotHanssemReferer: boolean = useMemo(() => {
    const utmSource = router?.query?.utm_source || '';
    if (
      // 레퍼러가 있으면서, 레퍼러가 한샘이 아니면서 utmSource가 있다면, 외부에서 온 것으로 판단
      reqHeader?.referer &&
      reqHeader.referer.indexOf('hanssem.com') <= -1 &&
      utmSource
    ) {
      return true;
    }

    return false;
  }, [reqHeader, router]);

  const handleBack = useCallback(() => {
    const firstPageValue = getSessionItem('firstPage');
    if (!isApp()) {
      if (
        isNotHanssemReferer ||
        (firstPageValue && firstPageValue === window.location.href)
      ) {
        router.push(`${process.env.NEXT_PUBLIC_STORE_MWEB_HOST}`);
        dispatch(removeReqHeader());
      } else {
        router.back();
      }
    } else {
      router.back();
    }
  }, [dispatch, isApp, isNotHanssemReferer, router]);

  const handleBackWithCloseAppPopup = useCallback(
    (args?: CloseWebPopUpData) => {
      handleBack();
      if (isApp()) {
        handleCloseWebPopUp({
          ...args,
        });
      }
    },
    [handleBack, handleCloseWebPopUp, isApp],
  );

  /**
   * app에서 백 할 때 사용할 함수
   */
  const handleInAppBack = useCallback(
    (args?: CloseWebPopUpData) => {
      // referer가 없으면 팝업을 닫는다.
      if (isEmptyReferer) {
        handleBackWithCloseAppPopup(args);
      } else {
        router.back();
      }
    },
    [handleBackWithCloseAppPopup, isEmptyReferer, router],
  );

  useEffect(() => {
    /***
    url로 랜딩한 첫번째 페이지인 경우
    백버튼시 통합메인으로 진입시키기 위해 세션에 저장
    ***/
    if (!isApp() && window.history.length === 1) {
      setSessionItem('firstPage', window.location.href);
    }
  }, [isApp]);

  return { handleBack, handleBackWithCloseAppPopup, handleInAppBack };
};

export default useBackHandler;
